"use client";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createSession, updateSession, listAgents, createAgentRecord, deleteAgentRecord, listKnowledgeCollections, createKnowledgeCollection, deleteKnowledgeCollection, deleteKnowledge } from "@/lib/api";
import AgentModal from "./AgentModal";
import KnowledgeModal from "./KnowledgeModal";
import { prettyProvider } from "@/lib/aiMap";

type KnowledgeItem = { id: string; type: "file" | "url" | "code"; name: string; url?: string; text?: string };
type Agent = { id: string; name: string; provider: string; model: string; system?: string; system_prompt?: string; description?: string; icon?: string; knowledge?: KnowledgeItem[] };

async function fetchAgentsFromServer(): Promise<Agent[]> {
  try {
    const items = await listAgents();
    return (items || []).map((x: any) => ({
      id: String(x.id ?? x.uuid ?? x._id ?? `${Date.now()}`),
      name: x.name ?? x.title ?? "",
      provider: x.provider ?? x.vendor ?? "auto",
      model: x.model ?? x.model_id ?? "openai/gpt-5",
      system: x.system ?? x.system_prompt ?? undefined,
      system_prompt: x.system ?? x.system_prompt ?? undefined, // Add both fields for compatibility
      description: x.description ?? x.desc ?? undefined,
      icon: x.icon_url ?? x.iconUrl ?? x.icon ?? undefined,
      knowledge: Array.isArray(x.knowledge) ? x.knowledge : undefined,
    }));
  } catch {
    // fallback: none
    return [];
  }
}

export default function ExplorePanel() {
  const [name, setName] = useState("");
  const [provider, setProvider] = useState("auto");
  const [model, setModel] = useState("openai/gpt-5");
  const [system, setSystem] = useState("");
  const [description, setDescription] = useState("");
  const [knowledge, setKnowledge] = useState<KnowledgeItem[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [busy, setBusy] = useState(false);
  const [kSets, setKSets] = useState<any[]>([]);
  const [kItems, setKItems] = useState<any[]>([]);
  const [kName, setKName] = useState("");
  const [kDesc, setKDesc] = useState("");
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showKnowledgeModal, setShowKnowledgeModal] = useState(false);

  useEffect(() => {
    (async () => {
      const agentList = await fetchAgentsFromServer();
      setAgents(agentList);
      // Also store in localStorage for persistence
      if (agentList.length > 0) {
        try { localStorage.setItem('agents', JSON.stringify(agentList)); } catch {}
      }
    })();
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const arr = await listKnowledgeCollections();
        setKSets(arr || []);
        // Also store in localStorage for persistence
        if (arr.length > 0) {
          try { localStorage.setItem('knowledge_collections', JSON.stringify(arr)); } catch {}
        }
      }
      catch {
        try {
          const raw = localStorage.getItem("knowledge_collections");
          setKSets(raw ? JSON.parse(raw) : []);
        } catch {
          setKSets([]);
        }
      }
    })();
  }, []);

  const canSave = name.trim().length > 0 && model.trim().length > 0;
  const providerOptions = useMemo(() => ["auto","openai","openrouter","anthropic","google","deepseek","meta","perplexity"], []);

  const onSave = async () => {
    if (!canSave) return;
    const payload: any = {
      name: name.trim(),
      provider,
      model: model.trim(),
      system: system.trim() || undefined,
      system_prompt: system.trim() || undefined, // Add both fields for compatibility
      description: description.trim() || undefined,
      knowledge: knowledge?.length ? knowledge : undefined
    };
    try {
      const saved = await createAgentRecord(payload);
      const normalized: Agent = {
        id: String(saved?.id ?? saved?.uuid ?? `${Date.now()}`),
        name: saved?.name ?? payload.name,
        provider: saved?.provider ?? payload.provider,
        model: saved?.model ?? payload.model,
        system: saved?.system ?? payload.system,
        system_prompt: saved?.system ?? saved?.system_prompt ?? payload.system, // Add both fields
        description: saved?.description ?? payload.description,
        icon: (saved as any)?.icon_url ?? (saved as any)?.icon ?? (payload as any)?.icon,
        knowledge: Array.isArray(saved?.knowledge) ? saved.knowledge : payload.knowledge,
      };
      const list = [normalized, ...agents].slice(0, 50); setAgents(list);
    } catch {
      const fallback: Agent = {
        id: `${Date.now()}`,
        ...payload,
        system_prompt: payload.system // Ensure both fields are present
      };
      setAgents(([fallback, ...agents]).slice(0, 50));
    }
    setName(""); setSystem(""); setDescription(""); setKnowledge([]);
  };

  const onStart = async (agent: Agent) => {
    setBusy(true);
    try {
      // Create a conversation bound to this Agent so backend can apply system_prompt automatically
      const s = await createSession(agent.name, {
        provider: agent.provider,
        model: agent.model,
        explore_type: 'agent',
        explore_name: String(agent.id),
        agent_id: (Number.isFinite(Number(agent.id)) ? Number(agent.id) : undefined) as any,
      } as any);
      const sid = Number((s as any)?.id);
      if (Number.isFinite(sid)) {
        try {
          localStorage.setItem('active_agent_id', String(agent.id));
          localStorage.setItem('active_explore_type', 'agent');
          localStorage.removeItem('active_knowledge_id');
        } catch {}
        try { await updateSession(sid, { title: agent.name, provider: agent.provider, model: agent.model }); } catch {}
        try { localStorage.setItem(`agent_system:${sid}`, agent.system || agent.system_prompt || ""); } catch {}
        try { localStorage.setItem(`agent_desc:${sid}`, agent.description || ""); } catch {}
        try { if (agent.knowledge) localStorage.setItem(`agent_knowledge:${sid}`, JSON.stringify(agent.knowledge)); } catch {}
        try { localStorage.setItem("active_session_id", String(sid)); window.dispatchEvent(new CustomEvent("sessionSwitched", { detail: sid })); } catch {}
        try { await fetch('/api/sessions/claim', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: sid }) }); } catch {}
        try { window.location.assign(`/c/${sid}`); } catch {}
      }
    } finally { setBusy(false); }
  };

  const onDelete = async (id: string) => {
    try { await deleteAgentRecord(id); } catch {}
    const list = agents.filter(a => a.id !== id); setAgents(list);
  };

  const saveKnowledgeSet = async () => {
    if (!kName.trim()) return;
    const rec = { name: kName.trim(), description: kDesc.trim() || undefined, items: [...kItems] };
    let saved: any = undefined;
    try { saved = await createKnowledgeCollection(rec); } catch {}
    const next = { id: String(saved?.id ?? `${Date.now()}`), ...(saved || rec) };
    const list = [next, ...kSets].slice(0, 100); setKSets(list);
    setKName(""); setKDesc(""); setKItems([]); setShowKnowledgeModal(false);
  };
  const deleteKnowledgeSet = async (id: string | number) => {
    try {
      if (typeof id === 'string' || isNaN(Number(id))) {
        await deleteKnowledge(String(id));
      } else {
        await deleteKnowledgeCollection(id);
      }
    } catch {}
    try { const arr = await listKnowledgeCollections(); setKSets(arr || []); return; } catch {}
    const list = kSets.filter((s: any) => String(s.id) !== String(id)); setKSets(list);
  };
  const startWithKnowledge = async (setAny: any) => {
    setBusy(true);
    try {
      // Create a conversation bound to this Knowledge so backend can apply instruction_knowledge and files
      const s = await createSession(setAny.name, {
        provider,
        model,
        explore_type: 'knowledge',
        explore_name: String(setAny.id ?? setAny.knowledge_id ?? ''),
        knowledge_id: String(setAny.id ?? setAny.knowledge_id ?? ''),
      } as any);
      const sid = Number((s as any)?.id);
      if (Number.isFinite(sid)) {
        // Do not store knowledge/system in localStorage; backend will assemble context by session/conversation id
        try { localStorage.removeItem('active_agent_id'); localStorage.removeItem('active_knowledge_id'); localStorage.removeItem('active_explore_type'); } catch {}
        try { localStorage.setItem("active_session_id", String(sid)); window.dispatchEvent(new CustomEvent("sessionSwitched", { detail: sid })); } catch {}
        try { await fetch('/api/sessions/claim', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: sid }) }); } catch {}
        try { window.location.assign(`/c/${sid}`); } catch {}
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="h-full w-full flex flex-col bg-gray-900 text-gray-100">
      <div className="h-12 flex items-center px-3 border-b border-gray-700/70 font-medium">Explore • Agent Builder</div>
      <div className="p-3 space-y-3 overflow-y-auto">
        <div className="text-right">
          <div className="inline-block relative">
            {/* <Button onClick={() => setShowCreateMenu((v) => !v)}>+ Create ▾</Button> */}
            {/* new pill-style create button for improved UI */}
            <button onClick={() => setShowCreateMenu((v) => !v)} className="ml-2 px-3 py-1.5 rounded-full bg-slate-800 text-gray-100 hover:bg-slate-700 border border-slate-600 text-sm">+ Create <span className="ml-1">▾</span></button>
            {showCreateMenu && (
              <div className="absolute right-0 mt-2 w-48 rounded-md border border-gray-700 bg-gray-800/95 shadow-xl z-10 backdrop-blur">
                <button className="w-full text-left px-3 py-2 hover:bg-gray-700" onClick={() => { setShowCreateMenu(false); setShowAgentModal(true); }}>Agent AI</button>
                <button className="w-full text-left px-3 py-2 hover:bg-gray-700" onClick={() => { setShowCreateMenu(false); setShowKnowledgeModal(true); }}>Knowledge</button>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="text-sm text-gray-300 mb-2">Your Agents</div>
          <div className="space-y-2">
            {agents.length === 0 && <div className="text-xs text-gray-400">No agents yet. Use Create.</div>}
            {agents.map((a) => (
              <div key={a.id} className="rounded-md border border-gray-700 p-3 bg-gray-800/60 min-h-[88px]">

                <div className="flex items-stretch gap-3 justify-between">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 aspect-square rounded-md bg-gray-700 flex items-center justify-center overflow-hidden shrink-0">
                      {a.icon ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={a.icon} alt={a.name} className="w-full h-full object-contain p-0.5" />
                      ) : (
                        <span className="text-sm text-gray-200">{String(a.name || 'A').trim().slice(0,1).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold line-clamp-1">{a.name}</div>
                      <div className="text-xs text-gray-400 mt-0.5 truncate">{prettyProvider(a.provider)} • {a.model}</div>
                      {a.description && <div className="text-xs text-gray-300 mt-1 line-clamp-2">{a.description}</div>}
                      {a.system && <div className="text-xs text-gray-300 mt-2 line-clamp-3 whitespace-pre-wrap">{a.system}</div>}
                    </div>
                  </div>
                  <div className="flex flex-col justify-between gap-2 items-stretch w-[140px] h-full self-stretch">
                    <Button variant="outline" size="lg" className="w-full" disabled={busy} onClick={() => onStart(a)}>Start Chat</Button>
                    <Button variant="destructive" size="lg" className="w-full" onClick={() => onDelete(a.id)}>Delete</Button>
                  </div>
                  </div>
                </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-sm text-gray-300 mb-2">Your Knowledge</div>
          <div >
          {!Array.isArray(kSets) || kSets.length === 0 ? (
            <div className="text-xs text-gray-400">ยังไม่มี Knowledge set — ใช้ปุ่ม Create เพื่อสร้าง</div>
          ) : (
            <div className="space-y-2">
              {kSets.map((s: any) => {
                const title = s.title ?? s.name ?? '';
                const desc = s.description ?? s.desc ?? '';
                const icon = s.icon_url ?? s.iconUrl ?? s.icon ?? '';
                const initials = String(title || 'K').trim().slice(0,1).toUpperCase();
                return (
                  <div key={s.id} className="rounded-md border border-gray-700 p-3 bg-gray-800/60 flex items-stretch justify-between min-h-[88px]">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 aspect-square rounded-md bg-gray-700 flex items-center justify-center overflow-hidden shrink-0">
                        {icon ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={icon} alt={title} className="w-full h-full object-contain p-0.5" />
                        ) : (
                          <span className="text-sm text-gray-200">{initials}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold line-clamp-1">{title}</div>
                        {!!desc && <div className="text-xs text-gray-400 line-clamp-2">{desc}</div>}
                        <div className="text-[11px] text-gray-500">รายการความรู้: {s.items?.length || 0}</div>
                      </div>
                    </div>
                    <div className="flex flex-col justify-between gap-2 items-stretch w-[140px] h-full self-stretch">
                      <Button variant="outline" size="lg" className="w-full" disabled={busy} onClick={() => startWithKnowledge(s)}>Start Chat</Button>
                      <Button variant="destructive" size="lg" className="w-full" onClick={() => deleteKnowledgeSet(s.id)}>Delete</Button>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
           </div>
        </div>
      </div>

      <AgentModal
        open={showAgentModal}
        onClose={() => setShowAgentModal(false)}
        providerOptions={providerOptions}
        defaultModel={model}
        onSave={async (agent: any) => {
          try {
            const saved = await createAgentRecord(agent);
            const next: Agent = {
              id: String(saved?.id ?? agent.id),
              name: saved?.name ?? agent.name,
              provider: saved?.provider ?? agent.provider,
              model: saved?.model ?? agent.model,
              system: saved?.system ?? saved?.system_prompt ?? agent.system ?? agent.system_prompt,
              system_prompt: saved?.system ?? saved?.system_prompt ?? agent.system ?? agent.system_prompt,
              description: saved?.description ?? agent.description,
            };
            setAgents(([next, ...agents]).slice(0, 50));
          } catch {
            const next: Agent = {
              id: agent.id,
              name: agent.name,
              provider: agent.provider,
              model: agent.model,
              system: agent.system ?? agent.system_prompt,
              system_prompt: agent.system ?? agent.system_prompt,
              description: agent.description,
            };
            setAgents(([next, ...agents]).slice(0, 50));
          }
        }}
      />

      <KnowledgeModal
        open={showKnowledgeModal}
        onClose={() => setShowKnowledgeModal(false)}
        onSaved={async (_setObj: any) => {
          try { const arr = await listKnowledgeCollections(); setKSets(arr || []); }
          catch { /* fallback handled elsewhere */ }
        }}
      />
    </div>
  );
}




